import type { AtsProvider } from '@kiku/integrations/types';
import { JOB_STATUS } from '@kiku/jobs/types';
import type { JobStatus } from '@kiku/jobs/types';
import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ChevronDown, Download, MoreHorizontal, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { DepartmentBreadcrumb } from '~/components/features/departments/department-breadcrumb';
import { JobsList } from '~/components/features/jobs/jobs-list';
import { PageContainer } from '~/components/layouts/page-container';
import { buttonVariants } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '~/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { useDocumentTitle } from '~/hooks/use-document-title';
import { useSlugIds } from '~/hooks/use-slug-ids';
import { api } from '~/lib/api';
import { i18n } from '~/lib/i18n';
import { resolveSlugIds } from '~/lib/slug-resolver';
import { cn } from '~/lib/utils';
import { atsConfigQueryOptions } from '~/queries/integrations.queries';

const JOBS_LIMIT = 20;

const jobsSearchSchema = z.object({
  search: z.string().optional().default(''),
  status: z.enum(JOB_STATUS).optional().default('active'),
  page: z.coerce.number().int().min(1).optional().default(1)
});

type JobsSearch = z.infer<typeof jobsSearchSchema>;

function jobsQueryOptions(orgId: string, deptId: string, params: JobsSearch) {
  return queryOptions({
    queryKey: ['jobs', orgId, deptId, params] as const,
    queryFn: async () => {
      const response = await api.api.org[':orgId'].department[':deptId'].jobs.$get({
        param: { orgId, deptId },
        query: {
          search: params.search,
          status: params.status,
          setupStatus: 'active',
          page: params.page,
          limit: JOBS_LIMIT
        }
      });
      if (!response.ok) {
        throw new Error(i18n.t('errors.unexpected'));
      }
      return response.json();
    }
  });
}

export const Route = createFileRoute('/_authenticated/org/$orgSlug/department/$deptSlug/jobs/')({
  validateSearch: jobsSearchSchema,
  loader: ({ context, params, location }) => {
    const { orgId, deptId } = resolveSlugIds(context.queryClient, params.orgSlug, params.deptSlug);
    if (orgId && deptId) {
      void context.queryClient.prefetchQuery(
        jobsQueryOptions(orgId, deptId, location.search as JobsSearch)
      );
    }
  },
  component: JobsPage
});

function JobsPage(): React.ReactNode {
  const { t } = useTranslation();
  useDocumentTitle(t('jobs.title'));
  const { orgSlug, deptSlug } = Route.useParams();
  const { orgId, deptId } = useSlugIds(orgSlug, deptSlug);
  const params = Route.useSearch();
  const navigate = useNavigate();
  const { data } = useQuery({
    ...jobsQueryOptions(orgId, deptId ?? '', params),
    placeholderData: keepPreviousData,
    enabled: !!orgId && !!deptId
  });
  const { data: integrationData } = useQuery({
    ...atsConfigQueryOptions(orgId),
    enabled: !!orgId
  });

  const jobs = data?.jobs ?? [];
  const total = data?.total ?? 0;
  const page = params.page;

  const atsProvider = integrationData?.config?.provider;
  const atsProviderLabels: Record<AtsProvider, string> = {
    teamtailor: t('integrations.ats.providers.teamtailor'),
    recman: t('integrations.ats.providers.recman')
  };
  const atsName = atsProvider ? atsProviderLabels[atsProvider] : undefined;

  function setStatus(status: JobStatus): void {
    void navigate({
      to: '/org/$orgSlug/department/$deptSlug/jobs',
      params: { orgSlug, deptSlug },
      search: (prev) => ({ ...prev, status, page: 1 })
    });
  }

  const newJob = (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger
          render={
            <DropdownMenuTrigger
              className={cn(
                buttonVariants(),
                'w-8 px-0 bg-transparent text-foreground hover:bg-muted',
                'sm:w-auto sm:px-2.5 sm:bg-primary sm:text-primary-foreground sm:hover:bg-primary/90'
              )}
            />
          }
        >
          <MoreHorizontal className="size-4 sm:hidden" />
          <span className="hidden sm:inline">{t('jobs.newJob')}</span>
          <ChevronDown className="hidden sm:inline size-4" />
        </TooltipTrigger>
        <TooltipContent side="bottom" className="sm:hidden">
          {t('jobs.newJob')}
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end" className="min-w-56">
        <DropdownMenuItem
          render={
            <Link to="/org/$orgSlug/department/$deptSlug/jobs/new" params={{ orgSlug, deptSlug }} />
          }
        >
          <Plus className="size-4" />
          {t('jobs.createNewJob')}
        </DropdownMenuItem>
        <DropdownMenuItem
          render={
            atsName ? (
              <Link
                to="/org/$orgSlug/department/$deptSlug/jobs/import"
                params={{ orgSlug, deptSlug }}
              />
            ) : (
              <Link to="/org/$orgSlug/integrations" params={{ orgSlug }} />
            )
          }
        >
          <Download className="size-4" />
          {t('jobs.importFromAts', { atsName: atsName ?? 'ATS' })}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <PageContainer
      breadcrumbs={[
        { content: <DepartmentBreadcrumb orgSlug={orgSlug} deptSlug={deptSlug} /> },
        { label: t('jobs.title') }
      ]}
      title={t('jobs.title')}
      actions={newJob}
    >
      <Tabs
        value={params.status}
        onValueChange={(value) => {
          setStatus(value as JobStatus);
        }}
        className="flex flex-1 min-h-0 flex-col"
      >
        <TabsList variant="line" className="w-full sm:w-fit sm:self-start">
          {JOB_STATUS.map((s) => (
            <TabsTrigger
              key={s}
              value={s}
              className="data-active:text-primary data-active:after:bg-primary"
            >
              {t(`jobs.tabs.${s}`)}
            </TabsTrigger>
          ))}
        </TabsList>

        {JOB_STATUS.map((s) => (
          <TabsContent key={s} value={s} className="flex flex-1 min-h-0 flex-col mt-5">
            <JobsList
              orgSlug={orgSlug}
              deptSlug={deptSlug}
              jobs={jobs}
              total={total}
              page={page}
              limit={JOBS_LIMIT}
              atsName={atsName}
            />
          </TabsContent>
        ))}
      </Tabs>
    </PageContainer>
  );
}
